import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut, Settings, FolderOpen, Users, MessageSquare, Plus, Trash2, Eye, EyeOff, GripVertical, Download, Upload, Archive, FileJson, Lock, Image, Film, Type, Images, Play, ChevronUp, ChevronDown } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import type { User } from '@supabase/supabase-js';
import { Input } from '@/components/ui/input';

const extractEmbedSrc = (input: string): string | null => {
  const match = input.match(/src=["']([^"']+)["']/);
  return match ? match[1] : null;
};

const Admin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [tab, setTab] = useState<'cases' | 'clients' | 'settings' | 'messages'>('cases');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (!session) navigate('/auth');
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate('/auth');
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  if (!user) return null;

  const tabs = [
    { key: 'cases' as const, label: 'Кейсы', icon: FolderOpen },
    { key: 'clients' as const, label: 'Клиенты', icon: Users },
    { key: 'messages' as const, label: 'Сообщения', icon: MessageSquare },
    { key: 'settings' as const, label: 'Настройки', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Admin header */}
      <header className="border-b border-border bg-card">
        <div className="container flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display font-bold text-gradient">STUDIO</Link>
            <span className="text-xs text-muted-foreground">/ Админка</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <LogOut size={14} /> Выйти
          </button>
        </div>
      </header>

      <div className="container py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                tab === key ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === 'cases' && <CasesTab />}
        {tab === 'clients' && <ClientsTab />}
        {tab === 'messages' && <MessagesTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

/* ======== CASES TAB ======== */
const CasesTab = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: cases } = useQuery({
    queryKey: ['admin-cases'],
    queryFn: async () => {
      const { data } = await supabase.from('cases').select('*').order('sort_order');
      return data || [];
    },
  });

  const createCase = useMutation({
    mutationFn: async () => {
      const slug = `project-${Date.now()}`;
      const { data } = await supabase.from('cases').insert([{
        slug,
        title_ru: 'Новый проект',
        title_en: 'New project',
        sort_order: (cases?.length || 0),
      }]).select().single();
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-cases'] });
      if (data) setEditing(data.id);
    },
  });

  const deleteCase = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('cases').delete().eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-cases'] }),
  });

  const togglePublish = useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      await supabase.from('cases').update({ is_published: published }).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-cases'] }),
  });

  const reorderCase = async (index: number, direction: 'up' | 'down') => {
    if (!cases) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= cases.length) return;
    const a = cases[index];
    const b = cases[swapIndex];
    await Promise.all([
      supabase.from('cases').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('cases').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    queryClient.invalidateQueries({ queryKey: ['admin-cases'] });
  };

  if (editing) {
    return <CaseEditor caseId={editing} onBack={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold">Кейсы</h2>
        <button
          onClick={() => createCase.mutate()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Добавить кейс
        </button>
      </div>

      <div className="space-y-3">
        {cases?.map((c, idx) => (
          <div key={c.id} className="flex items-center gap-4 p-4 bg-card border border-border rounded-xl">
            <div className="flex flex-col gap-0.5">
              <button onClick={() => reorderCase(idx, 'up')} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20">
                <ChevronUp size={14} />
              </button>
              <button onClick={() => reorderCase(idx, 'down')} disabled={idx === (cases?.length || 0) - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20">
                <ChevronDown size={14} />
              </button>
            </div>
            {c.thumbnail_url && (
              <img src={c.thumbnail_url} alt="" className="w-16 h-12 object-cover rounded-lg" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium truncate">{c.title_ru}</p>
                {c.password && (
                  <Lock size={14} className="text-accent shrink-0" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">/{c.slug}</p>
            </div>
            <button
              onClick={() => togglePublish.mutate({ id: c.id, published: !c.is_published })}
              className={`p-2 rounded-lg ${c.is_published ? 'text-primary' : 'text-muted-foreground'}`}
              title={c.is_published ? 'Опубликован' : 'Скрыт'}
            >
              {c.is_published ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
            <button onClick={() => setEditing(c.id)} className="px-3 py-1 text-sm bg-secondary rounded-lg hover:bg-muted">
              Редактировать
            </button>
            <button
              onClick={() => { if (confirm('Удалить кейс?')) deleteCase.mutate(c.id); }}
              className="p-2 text-muted-foreground hover:text-destructive"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ======== CASE EDITOR ======== */
const CaseEditor = ({ caseId, onBack }: { caseId: string; onBack: () => void }) => {
  const queryClient = useQueryClient();

  const { data: caseItem } = useQuery({
    queryKey: ['case-edit', caseId],
    queryFn: async () => {
      const { data } = await supabase.from('cases').select('*').eq('id', caseId).single();
      return data;
    },
  });

  const { data: sections } = useQuery({
    queryKey: ['case-sections-edit', caseId],
    queryFn: async () => {
      const { data } = await supabase.from('case_sections').select('*').eq('case_id', caseId).order('sort_order');
      return data || [];
    },
  });

  const [form, setForm] = useState<any>({});

  useEffect(() => {
    if (caseItem) setForm(caseItem);
  }, [caseItem]);

  const saveCase = useMutation({
    mutationFn: async () => {
      const { id, created_at, updated_at, ...rest } = form;
      await supabase.from('cases').update(rest).eq('id', caseId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['case-edit', caseId] }),
  });

  const addSection = useMutation({
    mutationFn: async (type: string) => {
      await supabase.from('case_sections').insert([{
        case_id: caseId,
        section_type: type,
        sort_order: (sections?.length || 0),
      }]);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['case-sections-edit', caseId] }),
  });

  const deleteSection = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('case_sections').delete().eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['case-sections-edit', caseId] }),
  });

  const updateSection = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await supabase.from('case_sections').update(data).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['case-sections-edit', caseId] }),
  });

  const reorderSection = async (index: number, direction: 'up' | 'down') => {
    if (!sections) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sections.length) return;
    const a = sections[index];
    const b = sections[swapIndex];
    await Promise.all([
      supabase.from('case_sections').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('case_sections').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    queryClient.invalidateQueries({ queryKey: ['case-sections-edit', caseId] });
  };

  const uploadFile = async (file: File, path: string) => {
    const ext = file.name.split('.').pop();
    const filePath = `${path}/${Date.now()}.${ext}`;
    const { data } = await supabase.storage.from('portfolio').upload(filePath, file);
    if (data) {
      const { data: urlData } = supabase.storage.from('portfolio').getPublicUrl(data.path);
      return urlData.publicUrl;
    }
    return null;
  };

  const sectionTypes = [
    { type: 'hero_video', label: 'Hero-видео', icon: Film },
    { type: 'hero_carousel', label: 'Hero-карусель', icon: Images },
    { type: 'hero_image', label: 'Hero-изображение', icon: Image },
    { type: 'carousel', label: 'Карусель', icon: Images },
    { type: 'video', label: 'Видео', icon: Film },
    { type: 'text_only', label: 'Текст', icon: Type },
    { type: 'image_only', label: 'Изображение', icon: Image },
  ];

  if (!caseItem) return null;

  return (
    <div>
      <button onClick={onBack} className="text-muted-foreground hover:text-foreground mb-6 text-sm">
        ← Назад к списку
      </button>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: case info */}
        <div className="space-y-4">
          <h2 className="text-lg font-display font-bold mb-4">Основная информация</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <Field label="Название (RU)" value={form.title_ru || ''} onChange={(v) => setForm({ ...form, title_ru: v })} />
            <Field label="Название (EN)" value={form.title_en || ''} onChange={(v) => setForm({ ...form, title_en: v })} />
          </div>
          <Field label="Slug" value={form.slug || ''} onChange={(v) => setForm({ ...form, slug: v })} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Категория (RU)" value={form.category_ru || ''} onChange={(v) => setForm({ ...form, category_ru: v })} />
            <Field label="Категория (EN)" value={form.category_en || ''} onChange={(v) => setForm({ ...form, category_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextArea label="Описание (RU)" value={form.description_ru || ''} onChange={(v) => setForm({ ...form, description_ru: v })} />
            <TextArea label="Описание (EN)" value={form.description_en || ''} onChange={(v) => setForm({ ...form, description_en: v })} />
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">Обложка</label>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const url = await uploadFile(file, 'cases');
                  if (url) setForm({ ...form, thumbnail_url: url });
                }
              }}
              className="text-sm text-muted-foreground"
            />
            {form.thumbnail_url && <img src={form.thumbnail_url} alt="" className="mt-2 w-full h-32 object-cover rounded-lg" />}
          </div>

          <Field label="Пароль (оставьте пустым для открытого доступа)" value={form.password || ''} onChange={(v) => setForm({ ...form, password: v || null })} />

          <h3 className="text-sm font-medium text-muted-foreground mt-6">SEO</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="SEO Title (RU)" value={form.seo_title_ru || ''} onChange={(v) => setForm({ ...form, seo_title_ru: v })} />
            <Field label="SEO Title (EN)" value={form.seo_title_en || ''} onChange={(v) => setForm({ ...form, seo_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextArea label="SEO Description (RU)" value={form.seo_description_ru || ''} onChange={(v) => setForm({ ...form, seo_description_ru: v })} />
            <TextArea label="SEO Description (EN)" value={form.seo_description_en || ''} onChange={(v) => setForm({ ...form, seo_description_en: v })} />
          </div>

          <button
            onClick={() => saveCase.mutate()}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm"
          >
            Сохранить
          </button>
        </div>

        {/* Right: sections */}
        <div>
          <h2 className="text-lg font-display font-bold mb-4">Секции</h2>

          <div className="flex flex-wrap gap-2 mb-6">
            {sectionTypes.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                onClick={() => addSection.mutate(type)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-secondary border border-border rounded-lg hover:border-primary/30 transition-colors"
              >
                <Icon size={14} /> + {label}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {sections?.map((s, idx) => (
              <SectionEditor
                key={s.id}
                section={s}
                onUpdate={(data) => updateSection.mutate({ id: s.id, data })}
                onDelete={() => { if (confirm('Удалить секцию?')) deleteSection.mutate(s.id); }}
                onMoveUp={idx > 0 ? () => reorderSection(idx, 'up') : undefined}
                onMoveDown={idx < (sections?.length || 0) - 1 ? () => reorderSection(idx, 'down') : undefined}
                uploadFile={uploadFile}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ======== SECTION EDITOR ======== */
const SectionEditor = ({ section, onUpdate, onDelete, onMoveUp, onMoveDown, uploadFile }: {
  section: any;
  onUpdate: (data: any) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  uploadFile: (file: File, path: string) => Promise<string | null>;
}) => {
  const [local, setLocal] = useState(section);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { setLocal(section); }, [section]);

  const save = () => {
    const { id, case_id, created_at, ...rest } = local;
    onUpdate(rest);
  };

  const typeConfig: Record<string, { label: string; icon: typeof Image }> = {
    hero_video: { label: 'Hero-видео', icon: Film },
    hero_carousel: { label: 'Hero-карусель', icon: Images },
    hero_image: { label: 'Hero-изображение', icon: Image },
    carousel: { label: 'Карусель', icon: Images },
    video: { label: 'Видео', icon: Film },
    text_only: { label: 'Текст', icon: Type },
    text: { label: 'Текст', icon: Type },
    image_only: { label: 'Изображение', icon: Image },
    image: { label: 'Изображение', icon: Image },
    full_image: { label: 'Полное изображение', icon: Image },
    text_image: { label: 'Текст + Изображение', icon: Image },
    quote: { label: 'Цитата', icon: Type },
  };

  const config = typeConfig[section.section_type] || { label: section.section_type, icon: Type };
  const TypeIcon = config.icon;

  // Preview rendering
  const renderPreview = () => {
    const type = section.section_type;
    
    if (['carousel', 'hero_carousel'].includes(type)) {
      const images = local.image_url ? local.image_url.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
      if (images.length > 0) {
        return (
          <div className="flex gap-1 overflow-hidden rounded h-12">
            {images.slice(0, 4).map((src: string, i: number) => (
              <img key={i} src={src} alt="" className="h-12 w-16 object-cover rounded" />
            ))}
            {images.length > 4 && <span className="flex items-center text-xs text-muted-foreground px-2">+{images.length - 4}</span>}
          </div>
        );
      }
      return <span className="text-xs text-muted-foreground/50">Нет изображений</span>;
    }
    
    if (type === 'video' || type === 'hero_video') {
      if (local.video_url) {
        return (
          <div className="flex items-center gap-2 p-2 bg-secondary rounded-lg">
            <Play size={14} className="text-primary" />
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">{local.video_url}</span>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-3 p-3 bg-secondary/50 border border-dashed border-border rounded-lg">
          <div className="w-10 h-10 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center shrink-0">
            <Play size={16} className="text-muted-foreground/40 ml-0.5" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">Видео-плейсхолдер</p>
            <p className="text-[10px] text-muted-foreground/50">Видео ещё не загружено — на сайте отображается заглушка</p>
          </div>
        </div>
      );
    }

    if (['text_only', 'text', 'quote'].includes(type)) {
      const text = local.text_ru || local.title_ru || '';
      return text ? <p className="text-xs text-muted-foreground truncate max-w-[300px]">{text}</p> : <span className="text-xs text-muted-foreground/50">Пустой текст</span>;
    }

    if (['image_only', 'image', 'full_image', 'text_image', 'hero_image'].includes(type)) {
      if (local.image_url) {
        const firstImg = local.image_url.split(',')[0].trim();
        return <img src={firstImg} alt="" className="h-12 w-20 object-cover rounded" />;
      }
      return <span className="text-xs text-muted-foreground/50">Нет изображения</span>;
    }

    return null;
  };

  return (
    <div className="p-4 bg-card border border-border rounded-xl">
      <div className="flex items-center justify-between mb-1 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <TypeIcon size={14} className="text-primary" />
          <span className="text-xs font-medium text-primary uppercase tracking-wider">
            {config.label}
          </span>
          <span className="text-xs text-muted-foreground">#{section.sort_order}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onMoveUp?.(); }} disabled={!onMoveUp} className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5">
            <ChevronUp size={14} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onMoveDown?.(); }} disabled={!onMoveDown} className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5">
            <ChevronDown size={14} />
          </button>
          <span className="text-xs text-muted-foreground mx-1">{expanded ? '▲' : '▼'}</span>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-muted-foreground hover:text-destructive p-0.5">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Inline preview when collapsed */}
      {!expanded && <div className="mt-2">{renderPreview()}</div>}

      {expanded && (
        <div className="space-y-3 mt-3 pt-3 border-t border-border">
          {/* Text fields for types that have text */}
          {['text_image', 'text_only', 'text', 'quote'].includes(section.section_type) && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Заголовок (RU)" value={local.title_ru || ''} onChange={(v) => setLocal({ ...local, title_ru: v })} small />
                <Field label="Заголовок (EN)" value={local.title_en || ''} onChange={(v) => setLocal({ ...local, title_en: v })} small />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextArea label="Текст (RU)" value={local.text_ru || ''} onChange={(v) => setLocal({ ...local, text_ru: v })} small />
                <TextArea label="Текст (EN)" value={local.text_en || ''} onChange={(v) => setLocal({ ...local, text_en: v })} small />
              </div>
            </>
          )}

          {/* Image upload for single-image types */}
          {['text_image', 'image_only', 'full_image', 'hero_image'].includes(section.section_type) && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Изображение</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const url = await uploadFile(file, 'sections');
                    if (url) setLocal({ ...local, image_url: url });
                  }
                }}
                className="text-xs text-muted-foreground"
              />
              {local.image_url && <img src={local.image_url} alt="" className="mt-1 w-full h-20 object-cover rounded" />}
            </div>
          )}

          {/* Multiple images for carousel / image types */}
          {['carousel', 'hero_carousel', 'image'].includes(section.section_type) && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Изображения (через запятую или загрузить)</label>
              <TextArea
                label=""
                value={local.image_url || ''}
                onChange={(v) => setLocal({ ...local, image_url: v })}
                small
              />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={async (e) => {
                  const files = e.target.files;
                  if (files) {
                    const urls: string[] = [];
                    for (const file of Array.from(files)) {
                      const url = await uploadFile(file, 'sections');
                      if (url) urls.push(url);
                    }
                    if (urls.length) {
                      const existing = local.image_url ? local.image_url.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                      setLocal({ ...local, image_url: [...existing, ...urls].join(', ') });
                    }
                  }
                }}
                className="text-xs text-muted-foreground mt-1"
              />
              {local.image_url && (() => {
                const imgs = local.image_url.split(',').map((s: string) => s.trim()).filter(Boolean);
                return (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {imgs.map((src: string, i: number) => (
                      <img key={i} src={src} alt="" className="h-16 w-20 object-cover rounded" />
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Video URL */}
          {(section.section_type === 'video' || section.section_type === 'hero_video') && (
            <>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Видео</label>
                <div className="flex gap-2 items-center mb-1">
                  <Input
                    placeholder="URL видео или embed-код"
                    value={local.video_url || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const src = extractEmbedSrc(val);
                      setLocal({ ...local, video_url: src || val || null });
                    }}
                    className="text-xs"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">или</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const ext = file.name.split('.').pop();
                        const filePath = `videos/${Date.now()}.${ext}`;
                        const { data } = await supabase.storage.from('portfolio').upload(filePath, file);
                        if (data) {
                          const { data: urlData } = supabase.storage.from('portfolio').getPublicUrl(data.path);
                          setLocal({ ...local, video_url: urlData.publicUrl });
                        }
                      }
                    }}
                    className="text-xs text-muted-foreground"
                  />
                </div>
                {local.video_url && (
                  <div className="mt-2 space-y-2">
                    {local.video_url.match(/\.(mp4|webm|mov|ogg)(\?|$)/i) ? (
                      <video src={local.video_url} className="w-full h-32 object-cover rounded-lg" muted autoPlay loop playsInline />
                    ) : (
                      <iframe src={local.video_url} className="w-full h-32 rounded-lg" allow="fullscreen" />
                    )}
                    <button
                      type="button"
                      onClick={() => setLocal({ ...local, video_url: null })}
                      className="text-xs text-destructive hover:underline"
                    >
                      Удалить видео
                    </button>
                  </div>
                )}
              </div>
              {section.section_type === 'hero_video' && (
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Изображение-заглушка (если нет видео)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const url = await uploadFile(file, 'sections');
                        if (url) setLocal({ ...local, image_url: url });
                      }
                    }}
                    className="text-xs text-muted-foreground"
                  />
                  {local.image_url && <img src={local.image_url} alt="" className="mt-1 w-full h-20 object-cover rounded" />}
                </div>
              )}
            </>
          )}

          {/* Layout for text_image */}
          {section.section_type === 'text_image' && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Макет</label>
              <select
                value={local.layout || 'left'}
                onChange={(e) => setLocal({ ...local, layout: e.target.value })}
                className="px-3 py-1.5 bg-secondary border border-border rounded text-sm text-foreground"
              >
                <option value="left">Текст слева</option>
                <option value="right">Текст справа</option>
              </select>
            </div>
          )}

          <button onClick={save} className="px-4 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium">
            Сохранить секцию
          </button>
        </div>
      )}
    </div>
  );
};

/* ======== CLIENTS TAB ======== */
const ClientsTab = () => {
  const queryClient = useQueryClient();

  const { data: clients } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: async () => {
      const { data } = await supabase.from('clients').select('*').order('sort_order');
      return data || [];
    },
  });

  const deleteClient = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('clients').delete().eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-clients'] }),
  });

  const uploadClients = async (files: FileList) => {
    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop();
      const filePath = `clients/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data } = await supabase.storage.from('portfolio').upload(filePath, file);
      if (data) {
        const { data: urlData } = supabase.storage.from('portfolio').getPublicUrl(data.path);
        await supabase.from('clients').insert([{
          name: file.name.replace(/\.[^.]+$/, ''),
          logo_url: urlData.publicUrl,
          sort_order: (clients?.length || 0),
        }]);
      }
    }
    queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold">Клиенты</h2>
        <label className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium cursor-pointer">
          <Plus size={16} /> Загрузить логотипы
          <input type="file" accept="image/*" multiple onChange={(e) => e.target.files && uploadClients(e.target.files)} className="hidden" />
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {clients?.map((c) => (
          <div key={c.id} className="relative group p-4 bg-card border border-border rounded-xl flex items-center justify-center aspect-square">
            <img src={c.logo_url} alt={c.name} className="max-w-full max-h-full object-contain" />
            <button
              onClick={() => deleteClient.mutate(c.id)}
              className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ======== MESSAGES TAB ======== */
const MessagesTab = () => {
  const { data: messages } = useQuery({
    queryKey: ['admin-messages'],
    queryFn: async () => {
      const { data } = await supabase.from('feedback_messages').select('*').order('created_at', { ascending: false });
      return data || [];
    },
  });

  return (
    <div>
      <h2 className="text-xl font-display font-bold mb-6">Сообщения</h2>
      <div className="space-y-3">
        {messages?.map((m) => (
          <div key={m.id} className="p-4 bg-card border border-border rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-medium text-sm">{m.name}</span>
              <span className="text-xs text-muted-foreground">{m.email}</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {new Date(m.created_at).toLocaleDateString('ru')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{m.message}</p>
          </div>
        ))}
        {(!messages || messages.length === 0) && (
          <p className="text-muted-foreground text-center py-8">Сообщений пока нет</p>
        )}
      </div>
    </div>
  );
};

/* ======== SETTINGS TAB ======== */
const SettingsTab = () => {
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const [form, setForm] = useState<any>({});

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const save = useMutation({
    mutationFn: async () => {
      const { id, created_at, updated_at, ...rest } = form;
      await supabase.from('site_settings').update(rest).eq('id', form.id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-settings'] }),
  });

  const uploadFile = async (file: File, path: string) => {
    const ext = file.name.split('.').pop();
    const filePath = `${path}/${Date.now()}.${ext}`;
    const { data } = await supabase.storage.from('portfolio').upload(filePath, file);
    if (data) {
      const { data: urlData } = supabase.storage.from('portfolio').getPublicUrl(data.path);
      return urlData.publicUrl;
    }
    return null;
  };

  if (!settings) return null;

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-display font-bold mb-6">Настройки сайта</h2>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Название сайта (RU)" value={form.site_name_ru || ''} onChange={(v) => setForm({ ...form, site_name_ru: v })} />
            <Field label="Название сайта (EN)" value={form.site_name_en || ''} onChange={(v) => setForm({ ...form, site_name_en: v })} />
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">Favicon</label>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const url = await uploadFile(file, 'settings');
                  if (url) setForm({ ...form, favicon_url: url });
                }
              }}
              className="text-sm text-muted-foreground"
            />
            {form.favicon_url && <img src={form.favicon_url} alt="" className="mt-1 w-8 h-8 object-contain" />}
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">Фоновое видео (Hero)</label>
            <div className="flex gap-2 items-center mb-1">
              <Input
                placeholder="URL видео или embed-код"
                value={form.hero_video_url || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const src = extractEmbedSrc(val);
                  setForm({ ...form, hero_video_url: src || val || null });
                }}
                className="text-sm"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">или</span>
              <input
                type="file"
                accept="video/mp4,video/webm"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const ext = file.name.split('.').pop();
                    const filePath = `hero/${Date.now()}.${ext}`;
                    const { data } = await supabase.storage.from('videos').upload(filePath, file);
                    if (data) {
                      const { data: urlData } = supabase.storage.from('videos').getPublicUrl(data.path);
                      setForm({ ...form, hero_video_url: urlData.publicUrl });
                    }
                  }
                }}
                className="text-sm text-muted-foreground"
              />
            </div>
            {form.hero_video_url && (
              <div className="mt-2 space-y-2">
                {form.hero_video_url.match(/\.(mp4|webm|mov|ogg)(\?|$)/i) ? (
                  <video src={form.hero_video_url} className="w-full h-32 object-cover rounded-lg" muted autoPlay loop playsInline />
                ) : (
                  <iframe src={form.hero_video_url} className="w-full h-32 rounded-lg" allow="fullscreen" />
                )}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, hero_video_url: null })}
                  className="text-xs text-destructive hover:underline"
                >
                  Удалить видео
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">SEO</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="SEO Title (RU)" value={form.seo_title_ru || ''} onChange={(v) => setForm({ ...form, seo_title_ru: v })} />
            <Field label="SEO Title (EN)" value={form.seo_title_en || ''} onChange={(v) => setForm({ ...form, seo_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextArea label="SEO Description (RU)" value={form.seo_description_ru || ''} onChange={(v) => setForm({ ...form, seo_description_ru: v })} />
            <TextArea label="SEO Description (EN)" value={form.seo_description_en || ''} onChange={(v) => setForm({ ...form, seo_description_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Ключевые слова (RU)" value={form.seo_keywords_ru || ''} onChange={(v) => setForm({ ...form, seo_keywords_ru: v })} />
            <Field label="Ключевые слова (EN)" value={form.seo_keywords_en || ''} onChange={(v) => setForm({ ...form, seo_keywords_en: v })} />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">OG Image (картинка для соцсетей)</label>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const url = await uploadFile(file, 'settings');
                  if (url) setForm({ ...form, og_image_url: url });
                }
              }}
              className="text-sm text-muted-foreground"
            />
            {form.og_image_url && <img src={form.og_image_url} alt="OG Image" className="mt-2 w-full max-w-md h-32 object-cover rounded-lg" />}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">«Обо мне» — главная страница</h3>
        <p className="text-xs text-muted-foreground mb-3">Короткий текст для секции на главной</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Заголовок (RU)" value={form.about_title_ru || ''} onChange={(v) => setForm({ ...form, about_title_ru: v })} />
            <Field label="Заголовок (EN)" value={form.about_title_en || ''} onChange={(v) => setForm({ ...form, about_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextArea label="Текст (RU)" value={form.about_text_ru || ''} onChange={(v) => setForm({ ...form, about_text_ru: v })} />
            <TextArea label="Текст (EN)" value={form.about_text_en || ''} onChange={(v) => setForm({ ...form, about_text_en: v })} />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Фото (общее)</label>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const url = await uploadFile(file, 'about');
                  if (url) setForm({ ...form, about_image_url: url });
                }
              }}
              className="text-sm text-muted-foreground"
            />
            {form.about_image_url && <img src={form.about_image_url} alt="" className="mt-1 w-32 h-32 object-cover rounded-lg" />}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">«Обо мне» — страница /about</h3>
        <p className="text-xs text-muted-foreground mb-3">Развёрнутый текст для отдельной страницы</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Заголовок (RU)" value={(form as any).about_page_title_ru || ''} onChange={(v) => setForm({ ...form, about_page_title_ru: v })} />
            <Field label="Заголовок (EN)" value={(form as any).about_page_title_en || ''} onChange={(v) => setForm({ ...form, about_page_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextArea label="Текст (RU)" value={(form as any).about_page_text_ru || ''} onChange={(v) => setForm({ ...form, about_page_text_ru: v })} />
            <TextArea label="Текст (EN)" value={(form as any).about_page_text_en || ''} onChange={(v) => setForm({ ...form, about_page_text_en: v })} />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">Hero-фразы</h3>
        <p className="text-xs text-muted-foreground mb-3">Вращающиеся фразы на главном экране</p>
        <div className="space-y-3">
          {(Array.isArray(form.hero_phrases_ru) ? form.hero_phrases_ru : []).map((_: string, idx: number) => (
            <div key={idx} className="flex gap-2 items-start">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Field small label={`Фраза ${idx + 1} (RU)`} value={(form.hero_phrases_ru as string[])?.[idx] || ''} onChange={(v) => {
                  const arr = [...(form.hero_phrases_ru as string[] || [])];
                  arr[idx] = v;
                  setForm({ ...form, hero_phrases_ru: arr });
                }} />
                <Field small label={`Фраза ${idx + 1} (EN)`} value={(form.hero_phrases_en as string[])?.[idx] || ''} onChange={(v) => {
                  const arr = [...(form.hero_phrases_en as string[] || [])];
                  arr[idx] = v;
                  setForm({ ...form, hero_phrases_en: arr });
                }} />
              </div>
              <button onClick={() => {
                const ru = [...(form.hero_phrases_ru as string[] || [])];
                const en = [...(form.hero_phrases_en as string[] || [])];
                ru.splice(idx, 1);
                en.splice(idx, 1);
                setForm({ ...form, hero_phrases_ru: ru, hero_phrases_en: en });
              }} className="mt-6 p-1 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => {
            setForm({
              ...form,
              hero_phrases_ru: [...(form.hero_phrases_ru as string[] || []), ''],
              hero_phrases_en: [...(form.hero_phrases_en as string[] || []), ''],
            });
          }} className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus size={14} /> Добавить фразу</button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">Заголовки секций</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Портфолио (RU)" value={form.portfolio_title_ru || ''} onChange={(v) => setForm({ ...form, portfolio_title_ru: v })} />
            <Field label="Портфолио (EN)" value={form.portfolio_title_en || ''} onChange={(v) => setForm({ ...form, portfolio_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Услуги (RU)" value={form.services_title_ru || ''} onChange={(v) => setForm({ ...form, services_title_ru: v })} />
            <Field label="Услуги (EN)" value={form.services_title_en || ''} onChange={(v) => setForm({ ...form, services_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Процесс (RU)" value={form.process_title_ru || ''} onChange={(v) => setForm({ ...form, process_title_ru: v })} />
            <Field label="Процесс (EN)" value={form.process_title_en || ''} onChange={(v) => setForm({ ...form, process_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Контакт заголовок (RU)" value={form.contact_title_ru || ''} onChange={(v) => setForm({ ...form, contact_title_ru: v })} />
            <Field label="Контакт заголовок (EN)" value={form.contact_title_en || ''} onChange={(v) => setForm({ ...form, contact_title_en: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Контакт подзаголовок (RU)" value={form.contact_subtitle_ru || ''} onChange={(v) => setForm({ ...form, contact_subtitle_ru: v })} />
            <Field label="Контакт подзаголовок (EN)" value={form.contact_subtitle_en || ''} onChange={(v) => setForm({ ...form, contact_subtitle_en: v })} />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">Футер</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Роль (RU)" value={form.footer_role_ru || ''} onChange={(v) => setForm({ ...form, footer_role_ru: v })} />
            <Field label="Роль (EN)" value={form.footer_role_en || ''} onChange={(v) => setForm({ ...form, footer_role_en: v })} />
          </div>
          <Field label="Telegram" value={(form.footer_socials as any)?.telegram || ''} onChange={(v) => setForm({ ...form, footer_socials: { ...(form.footer_socials as any || {}), telegram: v } })} />
          <Field label="Instagram" value={(form.footer_socials as any)?.instagram || ''} onChange={(v) => setForm({ ...form, footer_socials: { ...(form.footer_socials as any || {}), instagram: v } })} />
          <Field label="Behance" value={(form.footer_socials as any)?.behance || ''} onChange={(v) => setForm({ ...form, footer_socials: { ...(form.footer_socials as any || {}), behance: v } })} />
          <Field label="YouTube" value={(form.footer_socials as any)?.youtube || ''} onChange={(v) => setForm({ ...form, footer_socials: { ...(form.footer_socials as any || {}), youtube: v } })} />
          <Field label="LinkedIn" value={(form.footer_socials as any)?.linkedin || ''} onChange={(v) => setForm({ ...form, footer_socials: { ...(form.footer_socials as any || {}), linkedin: v } })} />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-display font-bold mb-4">Форма обратной связи</h3>
        <div className="space-y-4">
          <Field label="Email для уведомлений" value={form.feedback_email || ''} onChange={(v) => setForm({ ...form, feedback_email: v })} />
          <Field label="Telegram" value={form.feedback_telegram || ''} onChange={(v) => setForm({ ...form, feedback_telegram: v })} />
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <button
          onClick={() => save.mutate()}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm"
        >
          Сохранить настройки
        </button>
        <button
          onClick={() => {
            const title = form.seo_title_ru || form.site_name_ru || 'Portfolio';
            const desc = form.seo_description_ru || '';
            const favicon = form.favicon_url || '';
            const ogImage = form.og_image_url || '';
            const code = `<title>${title}</title>
<meta name="description" content="${desc}" />
<meta name="author" content="Kostya Kot" />
${favicon ? `<link rel="icon" type="image/png" href="${favicon}" />` : ''}

<meta property="og:title" content="${title}" />
<meta property="og:description" content="${desc}" />
<meta property="og:type" content="website" />
${ogImage ? `<meta property="og:image" content="${ogImage}" />` : ''}

<meta name="twitter:card" content="summary_large_image" />
${ogImage ? `<meta name="twitter:image" content="${ogImage}" />` : ''}`;
            navigator.clipboard.writeText(code).then(() => {
              alert('Meta-теги скопированы! Вставьте их в чат Lovable с просьбой обновить index.html');
            });
          }}
          className="px-6 py-2 bg-secondary text-foreground rounded-lg font-medium text-sm hover:bg-muted transition-colors"
        >
          📋 Скопировать meta-теги
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        После изменения SEO-настроек нажмите «Скопировать meta-теги» и вставьте код в чат Lovable — он обновит index.html для корректного отображения в соцсетях.
      </p>

      <MigrateVercelBlob />

      <ServicesEditor />
      <ProcessStepsEditor />

      <BackupRestore />
    </div>
  );
};

/* ======== MIGRATE VERCEL BLOB ======== */
const MigrateVercelBlob = () => {
  const [migrating, setMigrating] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const queryClient = useQueryClient();

  const addLog = (msg: string) => setLog(prev => [...prev, msg]);

  const isVercel = (url: string | null) => url?.includes('vercel-storage.com') ?? false;

  const downloadBlob = async (url: string): Promise<Blob | null> => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`${resp.status}`);
      return await resp.blob();
    } catch (e: any) {
      addLog(`❌ Не удалось скачать: ${url.slice(-40)} (${e.message})`);
      return null;
    }
  };

  const uploadToStorage = async (blob: Blob, bucket: string, path: string): Promise<string | null> => {
    const { data, error } = await supabase.storage.from(bucket).upload(path, blob, {
      contentType: blob.type || 'application/octet-stream',
      upsert: true,
    });
    if (error) {
      addLog(`❌ Ошибка загрузки ${path}: ${error.message}`);
      return null;
    }
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const migrateUrl = async (url: string, bucket: string, prefix: string, idx: number): Promise<string | null> => {
    const ext = url.match(/\.(jpg|jpeg|png|webp|gif|svg|mp4|webm)/i)?.[0] || '.jpg';
    const path = `${prefix}/${Date.now()}-${idx}${ext}`;
    const blob = await downloadBlob(url);
    if (!blob) return null;
    const newUrl = await uploadToStorage(blob, bucket, path);
    if (newUrl) addLog(`✅ ${url.slice(-30)} → Cloud`);
    return newUrl;
  };

  const migrate = async () => {
    setMigrating(true);
    setLog([]);

    // 1. site_settings
    addLog('📦 Миграция настроек сайта...');
    const { data: settings } = await supabase.from('site_settings').select('*').maybeSingle();
    if (settings) {
      const updates: Record<string, string> = {};
      for (const col of ['favicon_url', 'og_image_url', 'about_image_url'] as const) {
        if (isVercel(settings[col])) {
          const newUrl = await migrateUrl(settings[col]!, 'portfolio', `settings`, 0);
          if (newUrl) updates[col] = newUrl;
        }
      }
      if (isVercel(settings.hero_video_url)) {
        const newUrl = await migrateUrl(settings.hero_video_url!, 'videos', 'hero', 0);
        if (newUrl) updates['hero_video_url'] = newUrl;
      }
      if (Object.keys(updates).length > 0) {
        await supabase.from('site_settings').update(updates).eq('id', settings.id);
        addLog(`✅ Обновлены настройки: ${Object.keys(updates).join(', ')}`);
      }
    }

    // 2. case_sections
    addLog('📦 Миграция секций кейсов...');
    const { data: sections } = await supabase.from('case_sections').select('id, image_url, video_url');
    if (sections) {
      for (const section of sections) {
        const sectionUpdates: Record<string, string> = {};

        if (section.image_url && section.image_url.includes('vercel-storage.com')) {
          const urls = section.image_url.split(',').map((u: string) => u.trim());
          const newUrls: string[] = [];
          for (let i = 0; i < urls.length; i++) {
            if (isVercel(urls[i])) {
              const newUrl = await migrateUrl(urls[i], 'portfolio', `sections/${section.id}`, i);
              newUrls.push(newUrl || urls[i]);
            } else {
              newUrls.push(urls[i]);
            }
          }
          sectionUpdates['image_url'] = newUrls.join(', ');
        }

        if (isVercel(section.video_url)) {
          const newUrl = await migrateUrl(section.video_url!, 'videos', `sections/${section.id}`, 0);
          if (newUrl) sectionUpdates['video_url'] = newUrl;
        }

        if (Object.keys(sectionUpdates).length > 0) {
          await supabase.from('case_sections').update(sectionUpdates).eq('id', section.id);
        }
      }
    }

    // 3. cases thumbnails
    addLog('📦 Миграция обложек кейсов...');
    const { data: cases } = await supabase.from('cases').select('id, slug, thumbnail_url');
    if (cases) {
      for (const c of cases) {
        if (isVercel(c.thumbnail_url)) {
          const newUrl = await migrateUrl(c.thumbnail_url!, 'portfolio', `cases/${c.slug}`, 0);
          if (newUrl) await supabase.from('cases').update({ thumbnail_url: newUrl }).eq('id', c.id);
        }
      }
    }

    // 4. clients logos
    addLog('📦 Миграция логотипов клиентов...');
    const { data: clients } = await supabase.from('clients').select('id, name, logo_url');
    if (clients) {
      for (const client of clients) {
        if (isVercel(client.logo_url)) {
          const newUrl = await migrateUrl(client.logo_url, 'portfolio', 'clients', 0);
          if (newUrl) await supabase.from('clients').update({ logo_url: newUrl }).eq('id', client.id);
        }
      }
    }

    addLog('🎉 Миграция завершена!');
    setDone(true);
    setMigrating(false);
    queryClient.invalidateQueries();
  };

  return (
    <div className="border border-border rounded-lg p-4 space-y-3">
      <h3 className="text-lg font-display font-bold">🔄 Миграция медиа в Cloud</h3>
      <p className="text-xs text-muted-foreground">
        Перенос всех медиафайлов из Vercel Blob Storage в Lovable Cloud Storage.
        Процесс скачивает файлы через ваш браузер и загружает в облако.
      </p>
      {!done && (
        <button
          onClick={migrate}
          disabled={migrating}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {migrating ? '⏳ Идёт миграция...' : 'Начать миграцию'}
        </button>
      )}
      {log.length > 0 && (
        <div className="bg-black/50 rounded-lg p-3 max-h-64 overflow-y-auto text-xs font-mono space-y-0.5">
          {log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </div>
  );
};


const ServicesEditor = () => {
  const queryClient = useQueryClient();
  const { data: services } = useQuery({
    queryKey: ['admin-services'],
    queryFn: async () => {
      const { data } = await supabase.from('services').select('*').order('sort_order');
      return data || [];
    },
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<any>({});

  const startEdit = (s: any) => { setEditing(s.id); setForm(s); };
  const cancelEdit = () => { setEditing(null); setForm({}); };

  const saveService = async () => {
    const { id, created_at, ...rest } = form;
    await supabase.from('services').update(rest).eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['admin-services'] });
    cancelEdit();
  };

  const addService = async () => {
    await supabase.from('services').insert([{ title_ru: 'Новая услуга', title_en: 'New service', sort_order: (services?.length || 0) }]);
    queryClient.invalidateQueries({ queryKey: ['admin-services'] });
  };

  const deleteService = async (id: string) => {
    if (!confirm('Удалить услугу?')) return;
    await supabase.from('services').delete().eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['admin-services'] });
  };

  const reorder = async (index: number, direction: 'up' | 'down') => {
    if (!services) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= services.length) return;
    const a = services[index]; const b = services[swapIndex];
    await Promise.all([
      supabase.from('services').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('services').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    queryClient.invalidateQueries({ queryKey: ['admin-services'] });
  };

  return (
    <div className="border-t border-border pt-8 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-bold">Услуги</h3>
        <button onClick={addService} className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus size={14} /> Добавить</button>
      </div>
      <div className="space-y-3">
        {services?.map((s, idx) => (
          <div key={s.id} className="p-4 bg-card border border-border rounded-xl">
            {editing === s.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field small label="Название (RU)" value={form.title_ru || ''} onChange={(v) => setForm({ ...form, title_ru: v })} />
                  <Field small label="Название (EN)" value={form.title_en || ''} onChange={(v) => setForm({ ...form, title_en: v })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <TextArea small label="Описание (RU)" value={form.description_ru || ''} onChange={(v) => setForm({ ...form, description_ru: v })} />
                  <TextArea small label="Описание (EN)" value={form.description_en || ''} onChange={(v) => setForm({ ...form, description_en: v })} />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveService} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-lg">Сохранить</button>
                  <button onClick={cancelEdit} className="px-3 py-1 text-xs bg-secondary rounded-lg">Отмена</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => reorder(idx, 'up')} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp size={14} /></button>
                  <button onClick={() => reorder(idx, 'down')} disabled={idx === (services?.length || 0) - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown size={14} /></button>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{s.title_ru}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.description_ru?.slice(0, 80)}...</p>
                </div>
                <button onClick={() => startEdit(s)} className="px-3 py-1 text-xs bg-secondary rounded-lg hover:bg-muted">Ред.</button>
                <button onClick={() => deleteService(s.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ======== PROCESS STEPS EDITOR ======== */
const ProcessStepsEditor = () => {
  const queryClient = useQueryClient();
  const { data: steps } = useQuery({
    queryKey: ['admin-process-steps'],
    queryFn: async () => {
      const { data } = await supabase.from('process_steps').select('*').order('sort_order');
      return data || [];
    },
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<any>({});

  const startEdit = (s: any) => { setEditing(s.id); setForm(s); };
  const cancelEdit = () => { setEditing(null); setForm({}); };

  const saveStep = async () => {
    const { id, created_at, ...rest } = form;
    await supabase.from('process_steps').update(rest).eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['admin-process-steps'] });
    cancelEdit();
  };

  const addStep = async () => {
    const num = String((steps?.length || 0) + 1).padStart(2, '0');
    await supabase.from('process_steps').insert([{ number: num, title_ru: 'Новый этап', title_en: 'New step', sort_order: (steps?.length || 0) }]);
    queryClient.invalidateQueries({ queryKey: ['admin-process-steps'] });
  };

  const deleteStep = async (id: string) => {
    if (!confirm('Удалить этап?')) return;
    await supabase.from('process_steps').delete().eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['admin-process-steps'] });
  };

  const reorder = async (index: number, direction: 'up' | 'down') => {
    if (!steps) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= steps.length) return;
    const a = steps[index]; const b = steps[swapIndex];
    await Promise.all([
      supabase.from('process_steps').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('process_steps').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    queryClient.invalidateQueries({ queryKey: ['admin-process-steps'] });
  };

  return (
    <div className="border-t border-border pt-8 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-bold">Шаги процесса</h3>
        <button onClick={addStep} className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus size={14} /> Добавить</button>
      </div>
      <div className="space-y-3">
        {steps?.map((s, idx) => (
          <div key={s.id} className="p-4 bg-card border border-border rounded-xl">
            {editing === s.id ? (
              <div className="space-y-3">
                <Field small label="Номер" value={form.number || ''} onChange={(v) => setForm({ ...form, number: v })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field small label="Название (RU)" value={form.title_ru || ''} onChange={(v) => setForm({ ...form, title_ru: v })} />
                  <Field small label="Название (EN)" value={form.title_en || ''} onChange={(v) => setForm({ ...form, title_en: v })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <TextArea small label="Описание (RU)" value={form.description_ru || ''} onChange={(v) => setForm({ ...form, description_ru: v })} />
                  <TextArea small label="Описание (EN)" value={form.description_en || ''} onChange={(v) => setForm({ ...form, description_en: v })} />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveStep} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-lg">Сохранить</button>
                  <button onClick={cancelEdit} className="px-3 py-1 text-xs bg-secondary rounded-lg">Отмена</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => reorder(idx, 'up')} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp size={14} /></button>
                  <button onClick={() => reorder(idx, 'down')} disabled={idx === (steps?.length || 0) - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown size={14} /></button>
                </div>
                <span className="text-primary text-xs font-medium w-6">{s.number}</span>
                <div className="flex-1">
                  <p className="font-medium text-sm">{s.title_ru}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.description_ru?.slice(0, 80)}...</p>
                </div>
                <button onClick={() => startEdit(s)} className="px-3 py-1 text-xs bg-secondary rounded-lg hover:bg-muted">Ред.</button>
                <button onClick={() => deleteStep(s.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ======== BACKUP / RESTORE ======== */
const BackupRestore = () => {
  const [exportStatus, setExportStatus] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [urlMap, setUrlMap] = useState<Record<string, string> | null>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  // Collect all media URLs from data
  const collectUrls = (data: any): string[] => {
    const urls = new Set<string>();
    const fields = ['thumbnail_url', 'image_url', 'video_url', 'logo_url', 'favicon_url', 'hero_video_url', 'about_image_url'];
    const scan = (obj: any) => {
      if (!obj) return;
      if (Array.isArray(obj)) { obj.forEach(scan); return; }
      if (typeof obj === 'object') {
        for (const [key, val] of Object.entries(obj)) {
          if (fields.includes(key) && typeof val === 'string' && val.startsWith('http')) {
            // Handle comma-separated URLs (hero_carousel image_url)
            val.split(',').forEach(u => { const trimmed = u.trim(); if (trimmed) urls.add(trimmed); });
          }
          scan(val);
        }
      }
    };
    scan(data);
    return Array.from(urls);
  };

  const handleExportJson = async () => {
    setExportStatus('Экспорт данных...');
    try {
      const [cases, sections, clients, siteSettings] = await Promise.all([
        supabase.from('cases').select('*').order('sort_order'),
        supabase.from('case_sections').select('*').order('sort_order'),
        supabase.from('clients').select('*').order('sort_order'),
        supabase.from('site_settings').select('*'),
      ]);
      const backup = {
        version: 1,
        exported_at: new Date().toISOString(),
        cases: cases.data || [],
        case_sections: sections.data || [],
        clients: clients.data || [],
        site_settings: siteSettings.data || [],
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      saveAs(blob, `backup-${new Date().toISOString().slice(0, 10)}.json`);
      setExportStatus('JSON экспортирован!');
    } catch (e) {
      setExportStatus('Ошибка экспорта JSON');
      console.error(e);
    }
  };

  const handleExportMedia = async () => {
    setExportStatus('Сбор данных...');
    try {
      const [cases, sections, clients, siteSettings] = await Promise.all([
        supabase.from('cases').select('*'),
        supabase.from('case_sections').select('*'),
        supabase.from('clients').select('*'),
        supabase.from('site_settings').select('*'),
      ]);
      const allData = { cases: cases.data, case_sections: sections.data, clients: clients.data, site_settings: siteSettings.data };
      const urls = collectUrls(allData);
      
      if (urls.length === 0) {
        setExportStatus('Нет медиафайлов для экспорта');
        return;
      }

      const zip = new JSZip();
      const manifest: Record<string, string> = {};
      let done = 0;

      for (const url of urls) {
        try {
          setExportStatus(`Скачивание ${++done}/${urls.length}...`);
          const resp = await fetch(url);
          if (!resp.ok) continue;
          const blob = await resp.blob();
          const ext = url.split('.').pop()?.split('?')[0] || 'bin';
          const filename = `${done}.${ext}`;
          zip.file(filename, blob);
          manifest[url] = filename;
        } catch {
          console.warn('Failed to fetch:', url);
        }
      }

      zip.file('manifest.json', JSON.stringify(manifest, null, 2));
      setExportStatus('Создание ZIP...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `media-${new Date().toISOString().slice(0, 10)}.zip`);
      setExportStatus('Медиа экспортированы!');
    } catch (e) {
      setExportStatus('Ошибка экспорта медиа');
      console.error(e);
    }
  };

  const handleImportMedia = async (file: File) => {
    setImportStatus('Распаковка ZIP...');
    try {
      const zip = await JSZip.loadAsync(file);
      const manifestFile = zip.file('manifest.json');
      if (!manifestFile) {
        setImportStatus('manifest.json не найден в архиве');
        return;
      }
      const manifest: Record<string, string> = JSON.parse(await manifestFile.async('text'));
      const reverseManifest: Record<string, string> = {};
      // filename → old_url
      for (const [oldUrl, filename] of Object.entries(manifest)) {
        reverseManifest[filename] = oldUrl;
      }

      const newUrlMap: Record<string, string> = {};
      const entries = Object.entries(manifest);
      let done = 0;

      for (const [oldUrl, filename] of entries) {
        try {
          setImportStatus(`Загрузка ${++done}/${entries.length}...`);
          const zipFile = zip.file(filename);
          if (!zipFile) continue;
          const blob = await zipFile.async('blob');
          const storagePath = `backup-restore/${Date.now()}-${filename}`;
          const { data } = await supabase.storage.from('portfolio').upload(storagePath, blob);
          if (data) {
            const { data: urlData } = supabase.storage.from('portfolio').getPublicUrl(data.path);
            newUrlMap[oldUrl] = urlData.publicUrl;
          }
        } catch {
          console.warn('Failed to upload:', filename);
        }
      }

      setUrlMap(newUrlMap);
      setImportStatus(`Медиа загружены (${Object.keys(newUrlMap).length} файлов). Теперь загрузите JSON.`);
    } catch (e) {
      setImportStatus('Ошибка импорта медиа');
      console.error(e);
    }
  };

  const replaceUrls = (obj: any, map: Record<string, string>): any => {
    if (!obj) return obj;
    if (typeof obj === 'string') return obj;
    if (Array.isArray(obj)) return obj.map(item => replaceUrls(item, map));
    const result: any = {};
    const urlFields = ['thumbnail_url', 'image_url', 'video_url', 'logo_url', 'favicon_url', 'hero_video_url', 'about_image_url'];
    for (const [key, val] of Object.entries(obj)) {
      if (urlFields.includes(key) && typeof val === 'string') {
        // Handle comma-separated URLs
        if (val.includes(',')) {
          result[key] = val.split(',').map(u => {
            const trimmed = u.trim();
            return map[trimmed] || trimmed;
          }).join(', ');
        } else {
          result[key] = map[val] || val;
        }
      } else {
        result[key] = replaceUrls(val, map);
      }
    }
    return result;
  };

  const handleImportJson = async (file: File) => {
    setImportStatus('Чтение JSON...');
    try {
      const text = await file.text();
      let backup = JSON.parse(text);

      // Replace URLs if media was imported
      if (urlMap && Object.keys(urlMap).length > 0) {
        backup = replaceUrls(backup, urlMap);
      }

      // Import order: site_settings → clients → cases → case_sections
      // 1. Site settings
      if (backup.site_settings?.length) {
        setImportStatus('Импорт настроек...');
        for (const item of backup.site_settings) {
          const { created_at, updated_at, ...rest } = item;
          await supabase.from('site_settings').upsert(rest, { onConflict: 'id' });
        }
      }

      // 2. Clients — clear and re-insert
      if (backup.clients?.length) {
        setImportStatus('Импорт клиентов...');
        await supabase.from('clients').delete().neq('id', '00000000-0000-0000-0000-000000000000');
        for (const item of backup.clients) {
          const { created_at, ...rest } = item;
          await supabase.from('clients').insert(rest);
        }
      }

      // 3. Cases
      if (backup.cases?.length) {
        setImportStatus('Импорт кейсов...');
        // Delete sections first (FK dependency)
        await supabase.from('case_sections').delete().neq('id', '00000000-0000-0000-0000-000000000000');
        await supabase.from('cases').delete().neq('id', '00000000-0000-0000-0000-000000000000');
        for (const item of backup.cases) {
          const { created_at, updated_at, ...rest } = item;
          await supabase.from('cases').insert(rest);
        }
      }

      // 4. Case sections
      if (backup.case_sections?.length) {
        setImportStatus('Импорт секций...');
        for (const item of backup.case_sections) {
          const { created_at, ...rest } = item;
          await supabase.from('case_sections').insert(rest);
        }
      }

      setImportStatus('Импорт завершён!');
      setUrlMap(null);
    } catch (e) {
      setImportStatus('Ошибка импорта JSON');
      console.error(e);
    }
  };

  return (
    <div className="border-t border-border pt-8 mt-8">
      <h3 className="text-lg font-display font-bold mb-6">Резервное копирование</h3>

      {/* Export */}
      <div className="mb-8">
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Экспорт</h4>
        <div className="flex gap-3">
          <button
            onClick={handleExportJson}
            className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-sm hover:border-primary/30 transition-colors"
          >
            <FileJson size={16} /> Скачать данные (JSON)
          </button>
          <button
            onClick={handleExportMedia}
            className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-sm hover:border-primary/30 transition-colors"
          >
            <Archive size={16} /> Скачать медиа (ZIP)
          </button>
        </div>
        {exportStatus && <p className="text-xs text-muted-foreground mt-2">{exportStatus}</p>}
      </div>

      {/* Import */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Импорт</h4>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2">Шаг 1: Загрузите архив с медиафайлами</p>
            <input
              ref={mediaInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImportMedia(f);
              }}
            />
            <button
              onClick={() => mediaInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-sm hover:border-primary/30 transition-colors"
            >
              <Upload size={16} /> Загрузить ZIP
            </button>
            {urlMap && (
              <p className="text-xs text-primary mt-1">✓ Медиа загружены ({Object.keys(urlMap).length} файлов)</p>
            )}
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2">Шаг 2: Загрузите файл с данными</p>
            <input
              ref={jsonInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  if (!urlMap && !confirm('Медиа не загружены. Ссылки на файлы сохранятся как есть. Продолжить?')) return;
                  handleImportJson(f);
                }
              }}
            />
            <button
              onClick={() => jsonInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-sm hover:border-primary/30 transition-colors"
            >
              <Download size={16} /> Загрузить JSON
            </button>
          </div>
        </div>
        {importStatus && <p className="text-xs text-muted-foreground mt-3">{importStatus}</p>}
      </div>
    </div>
  );
};

/* ======== SHARED FIELDS ======== */
const Field = ({ label, value, onChange, small }: { label: string; value: string; onChange: (v: string) => void; small?: boolean }) => (
  <div>
    <label className={`block ${small ? 'text-xs' : 'text-sm'} text-muted-foreground mb-1`}>{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full ${small ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30`}
    />
  </div>
);

const TextArea = ({ label, value, onChange, small }: { label: string; value: string; onChange: (v: string) => void; small?: boolean }) => (
  <div>
    <label className={`block ${small ? 'text-xs' : 'text-sm'} text-muted-foreground mb-1`}>{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={small ? 2 : 3}
      className={`w-full ${small ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none`}
    />
  </div>
);

export default Admin;
